import { ChangeDetectorRef, Component, OnInit, input, output, inject, effect, DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, Validators } from "@angular/forms";
import { EvaluationService, UpdateEvalPriceRequest } from "@app-services/evaluation.service";
import { ToastrService } from "ngx-toastr";
import {
  StatusEvaluation,
  HistoryItemType,
  EvaluationVehicle,
  BuyPriceSummary,
  SellPriceSummary,
  NegotiationStatus,
} from "@app-model/evaluation";
import { debounceTime, finalize } from "rxjs/operators";
import { ChangeStatusEvaluation } from "@app-components/evaluation/evaluation-vehicle-detail/evaluation-vehicle-detail.component";
import { UserInfo, Role, LayoutVars, Permission } from "@app/store/session/session.store";
import { SessionService } from "@app/store/session/session.service";
import { EvaluationPriceModalComponent } from "@app-components/evaluation/evaluation-vehicle-detail/evaluation-price-modal/evaluation-price-modal.component";
import { MatDialog } from "@angular/material/dialog";
import { DialogEvaluationNegotiationStatusComponent } from "@app-components/evaluation/evaluation-vehicle-detail/dialog/dialog-evaluation-negotiation-status/dialog-evaluation-negotiation-status.component";
import {
  DialogNegotiationStatusCardComponent,
  DialogNegotiationStatusCardResult,
} from "@app-components/evaluation/evaluation-vehicle-detail/dialog/dialog-negotiation-status-card/dialog-negotiation-status-card.component";
import { SharedAnimations } from "@app-shared/animations/shared-animations";
import { OpenService } from "@app-services/open.service";
import { CommonModule } from "@angular/common";
import { ReactiveFormsModule } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatIconModule } from "@angular/material/icon";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { CurrencyMaskModule } from "ng2-currency-mask";
import { DashFipeModule } from "@app-components/dash-fipe/dash-fipe.module";
import { toPriceCentValue } from "@app-utils/eval-price.utils";
import { isFipeOutOfExpectedRange } from "@app-utils/fipe-comparison.utils";
import { SmallFeatureAnnouncementComponent } from "@app-components/small-feature-announcement/small-feature-announcement.component";
import { FipeComparisonBadgeComponent } from "@app-components/fipe-comparison-badge/fipe-comparison-badge.component";

const FIPE_ALERT_DEBOUNCE_MS = 1000;
@Component({
  selector: "app-card-evaluation-sold-price",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCheckboxModule,
    CurrencyMaskModule,
    DashFipeModule,
    SmallFeatureAnnouncementComponent,
    FipeComparisonBadgeComponent,
  ],
  templateUrl: "./card-evaluation-sold-price.component.html",
  styleUrls: ["./card-evaluation-sold-price.component.scss"],
  animations: [SharedAnimations]
})
export class CardEvaluationSoldPriceComponent implements OnInit {
  readonly vehicle = input.required<EvaluationVehicle>();
  readonly revisionEstimated = input<number>(0);
  readonly molicarPrice = input<number>(0);

  readonly clickConfirmBought = output<void>();
  readonly clickArchive = output<void>();
  readonly changeStatus = output<ChangeStatusEvaluation>();
  readonly updatePrice = output<UpdateEvalPriceRequest>();
  readonly clickDetailMolicar = output<void>();
  readonly clickConfirmReceipt = output<void>();
  readonly clickReturnToNegotiation = output<void>();

  userInfo: UserInfo | null = null;
  showBoxEditPrice = false;
  loadingReturnPricing = false;
  loadingUpdatePricing = false;
  readonly commentGiveBack = new FormControl<string>("", [Validators.required]);
  readonly checkboxResposability = new FormControl<boolean>(false, [
    Validators.required,
    Validators.requiredTrue,
  ]);
  readonly checkboxInterventionPrice = new FormControl<boolean>(false);
  readonly commentClosePricing = new FormControl<string>("");
  readonly destination = new FormControl<string>("TRADE_IN", [Validators.required]);
  readonly price = new FormControl<number>(0, [Validators.required]);
  readonly minSellPrice = new FormControl<number>(0);
  readonly listPrice = new FormControl<number>(0);
  readonly factoryBonus = new FormControl<number>(0);
  readonly interventionPrice = new FormControl<number>(0);
  fipePrice = 0;
  showGiveBack = false;
  loading = false;
  showFooter = true;
  showFactoryBonus = false;
  canEditFactoryBonus = false;

  animationState = 'out';
  layoutVars: LayoutVars | null = null;
  fipeOutOfRangeAlertVisible = false;

  private readonly evaluationService = inject(EvaluationService);
  private readonly toastrService = inject(ToastrService);
  private readonly sessionService = inject(SessionService);
  private readonly openService = inject(OpenService);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      this.vehicle(); // Track changes
      this.initializeFormValues();
      this.checkPricingResponsibility();
    });
  }

  ngOnInit(): void {
    this.initializeFormValues();
    this.loadLayoutVars();
    this.loadUserInfo();
    this.setupFipeOutOfRangeAlert();
  }

  showStatusNegotiationTooltip(): boolean {
    return this.userInfo && this.userInfo.hasPermission(Permission.CHANGE_NEGOTIATION_STATUS);
  }

  private checkPricingResponsibility(): void {
    if (!this.userInfo) return;
    const v = this.vehicle();
    const isResponsible = !v.pricingUserId || v.pricingUserId === this.userInfo.id;
    const canEditByBackend = v.canEditPrice === true;

    if (!canEditByBackend && !isResponsible) {
      this.price.disable();
      this.factoryBonus.disable();
      this.interventionPrice.disable();
      this.minSellPrice.disable();
      this.listPrice.disable();
      this.commentClosePricing.disable();
    } else {
      this.price.enable();
      this.minSellPrice.enable();
      this.listPrice.enable();
      this.commentClosePricing.enable();
      this.updateFactoryBonusPermissions();
    }
  }

  private initializeFormValues(): void {
    const v = this.vehicle();
    this.price.setValue(v.price, { emitEvent: false });
    this.factoryBonus.setValue(v.factoryBonus, { emitEvent: false });
    this.interventionPrice.setValue(v.interventionPrice, { emitEvent: false });
    this.minSellPrice.setValue(v.minSellPrice, { emitEvent: false });
    this.listPrice.setValue(v.listPrice, { emitEvent: false });
    this.fipePrice = v.fipePrice ?? 0;
    this.syncFipeOutOfRangeAlert();
  }

  private setupFipeOutOfRangeAlert(): void {
    this.price.valueChanges
      .pipe(
        debounceTime(FIPE_ALERT_DEBOUNCE_MS),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.syncFipeOutOfRangeAlert());
  }

  onPriceBlur(): void {
    this.syncFipeOutOfRangeAlert();
  }

  private syncFipeOutOfRangeAlert(): void {
    this.fipeOutOfRangeAlertVisible = isFipeOutOfExpectedRange(
      this.price.value,
      this.fipePrice,
    );
    this.cdr.markForCheck();
  }

  private loadLayoutVars(): void {
    this.openService.getLayoutVars().subscribe(layoutVars => {
      this.layoutVars = layoutVars;
    });
  }

  private loadUserInfo(): void {
    this.sessionService.getUser().subscribe(user => {
      this.userInfo = user;
      this.updateFactoryBonusPermissions();
      this.checkPricingResponsibility();
    });
  }

  private updateFactoryBonusPermissions(): void {
    if (!this.userInfo) return;
    const v = this.vehicle();
    const isResponsible = !v.pricingUserId || v.pricingUserId === this.userInfo.id;
    const canEditByBackend = v.canEditPrice === true;
    const hasPermission = this.userInfo.hasAnyPermission(Permission.EDIT_EVALUATION_PRICE);

    this.showFactoryBonus =
      this.userInfo.hasRoles('ADMIN', 'OEM_DEALER_MANAGER', 'SELLER_MANAGER')
      || (this.userInfo.hasRoles('DESK_BUY', 'DESK_SELL') && (v.factoryBonus ?? 0) > 0)
      || (this.userInfo.hasRole(Role.EVAL_DEALER_MANAGER) && this.userInfo.evalDealersIds.includes(v.evalDealerId))
      || hasPermission;

    this.canEditFactoryBonus = (canEditByBackend || isResponsible) && (this.userInfo.hasRoles('ADMIN', 'OEM_DEALER_MANAGER', 'SELLER_MANAGER') 
    || hasPermission);
    
    if (this.canEditFactoryBonus) {
      this.factoryBonus.enable();
      this.interventionPrice.enable();
    } else {
      this.factoryBonus.disable();
      this.interventionPrice.disable();
    }
  }

  finalPurchasePrice(): number {
    const v = this.vehicle();
    return (v.price ?? 0) + (v.factoryBonus ?? 0) + (v.interventionPrice ?? 0);
  }

  hasPriceComposition(): boolean {
    const v = this.vehicle();
    return (v.factoryBonus ?? 0) > 0 || (v.interventionPrice ?? 0) > 0;
  }

  onClickGiveBack = (): void => {
    this.showGiveBack = !this.showGiveBack;
  };

  toggleShowBoxEditPrice = (): void => {
    this.showBoxEditPrice = !this.showBoxEditPrice;
  };

  /** Arrow functions para preservar `this` quando passadas ao ng-template #btnLoading */
  onClickConfirmBought = (): void => {
    this.clickConfirmBought.emit();
  };

  onClickArchive = (): void => {
    this.clickArchive.emit();
  };

  onClickConfirmReceipt = (): void => {
    this.clickConfirmReceipt.emit();
  };

  onClickReturnToNegotiation = (): void => {
    this.clickReturnToNegotiation.emit();
  };

  onClickReturnPricing = (): void => {
    this.executeReturnToPricing();
  };

  private executeReturnToPricing(): void {
    this.loadingReturnPricing = true;
    const comments = this.commentGiveBack.value ?? '';

    this.evaluationService
      .returnToPricing(this.vehicle().id, comments)
      .subscribe({
        next: (response) => this.handleReturnToPricingResponse(response, comments),
        error: () => {
          this.loadingReturnPricing = false;
          this.showReturnToPricingError();
        }
      });
  }

  private handleReturnToPricingResponse(response: { success: boolean; status?: string }, comments: string): void {
    if (!response.success) {
      this.loadingReturnPricing = false;
      this.showReturnToPricingError(response.status);
      return;
    }

    this.loadingReturnPricing = false;
    this.showReturnToPricingSuccess();
    this.emitReturnToPricingStatus(comments);
  }

  private showReturnToPricingSuccess(): void {
    this.toastrService.success(
      "Captação devolvida para precificação",
      undefined,
      { progressBar: true }
    );
  }

  private showReturnToPricingError(status?: string): void {
    const message = this.getReturnToPricingErrorMessage(status);
    this.toastrService.error(message, undefined, { progressBar: true });
  }

  private getReturnToPricingErrorMessage(status: string | undefined): string {
    switch (status) {
      case "VEHICLE_NOT_FOUND":
        return "Avaliação não encontrada.";
      case "DONT_OWN_VEHICLE":
        return "Você não tem acesso a esta avaliação.";
      case "CANT_UPDATE":
        return "Não é possível devolver esta captação para precificação.";
      default:
        return "Falha ao devolver para precificação. Tente novamente mais tarde.";
    }
  }

  private emitReturnToPricingStatus(comments: string): void {
    const changeStatus: ChangeStatusEvaluation = {
      status: StatusEvaluation.PRICING, 
      itemHistory: {
        createdAt: new Date(),
        type: HistoryItemType.RETURNED_TO_PRICING,
        comments,
      },
      pricingUserId: null,
      pricingUserName: null,
      pricingStartedAt: null,
    };
    this.changeStatus.emit(changeStatus);
  }

  onClickUpdatePrice = (): void => {
    this.loadingUpdatePricing = true;
    const req = this.buildUpdatePriceRequest();

    this.evaluationService
      .updatePrice(req)
      .pipe(finalize(() => this.loadingUpdatePricing = false))
      .subscribe({
        next: (response) => this.handleUpdatePriceResponse(response, req),
        error: () => this.showUpdatePriceError()
      });
  };

  private buildUpdatePriceRequest(): UpdateEvalPriceRequest {
    const v = this.vehicle();
    const req = new UpdateEvalPriceRequest();
    req.price = this.price.value ?? 0;
    req.priceCent = toPriceCentValue(req.price) ?? undefined;
    req.factoryBonus = this.factoryBonus.value ?? 0;
    req.interventionPrice = this.interventionPrice.value ?? 0;
    req.id = v.id;
    req.destination = v.destination;
    req.comments = this.commentClosePricing.value ?? '';
    req.minSellPrice = this.minSellPrice.value ?? 0;
    req.listPrice = this.listPrice.value ?? 0;
    req.interventionPriceAccepted = this.checkboxInterventionPrice.value ?? false;
    return req;
  }

  private handleUpdatePriceResponse(response: { success: boolean }, req: UpdateEvalPriceRequest): void {
    if (response.success) {
      const v = this.vehicle();
      v.price = req.price;
      v.priceCent = req.priceCent;
      v.factoryBonus = req.factoryBonus;
      v.interventionPrice = req.interventionPrice;
      v.minSellPrice = req.minSellPrice;
      v.listPrice = req.listPrice;

      this.showBoxEditPrice = false;
      this.showUpdatePriceSuccess();
      this.updatePrice.emit(req);
      this.cdr.detectChanges();
      return;
    }
    this.showUpdatePriceError();
  }

  private showUpdatePriceSuccess(): void {
    this.toastrService.success("Preço atualizado com sucesso.", undefined, {
      progressBar: true,
    });
  }

  private showUpdatePriceError(): void {
    this.toastrService.error(
      "Falha ao atualizar o preço. Tente novamente mais tarde.",
      undefined,
      { tapToDismiss: true }
    );
  }

  canSaveUpdatedPrice(): boolean {
    return this.checkboxResposability.valid && this.price.valid;
  }

  hasPermissionEditPrice(): boolean {
    const v = this.vehicle();
    if (v.status === StatusEvaluation.ARCHIVED) {
      return false;
    }

    if (v.canEditPrice === true) {
      return true;
    }
    if (v.canEditPrice === false) {
      return false;
    }
    /* Fallback: quando backend não envia canEditPrice (retrocompatibilidade) */
    if (!this.userInfo || v.status !== StatusEvaluation.NEGOTIATION) {
      return false;
    }
    const isResponsible = !v.pricingUserId || v.pricingUserId === this.userInfo.id;
    if (!isResponsible) return false;
    const hasRole = !!this.userInfo?.hasRoles(
      Role.ADMIN,
      Role.OEM_DEALER_MANAGER,
      Role.SELLER_MANAGER,
      Role.DESK_BUY
    ) || (!!this.userInfo?.hasRole(Role.EVAL_DEALER_MANAGER) && this.userInfo.evalDealersIds.includes(v.evalDealerId));
    const hasPermission = !!this.userInfo?.hasAnyPermissionOrLegacy(
      Permission.EDIT_EVALUATION_PRICE,
      Permission.EDIT_EVALUATION,
      Permission.PRICE_EVALUATION
    );
    return hasRole || hasPermission;
  }

  hasPermissionBackPricing(): boolean {
    const v = this.vehicle();
    if (!this.userInfo || 
        v.status !== StatusEvaluation.NEGOTIATION || 
        v.blockEvalReturnToDeskBuy) {
      return false;
    }

    const hasLegacyRoles = this.userInfo.hasRoles(Role.ADMIN, Role.SELLER_MANAGER, Role.DESK_BUY) ||
      (this.userInfo.hasRole(Role.EVAL_DEALER_MANAGER) && 
       this.userInfo.evalDealersIds.includes(v.evalDealerId));

    const hasPermission = this.userInfo.hasAnyPermission(Permission.RETURN_TO_PRICING);

    return hasLegacyRoles || hasPermission;
  }

  hasPermissionConfirmPurchase(): boolean {
    return this.userInfo?.hasPermissionOrLegacy(Permission.CONFIRM_EVALUATION_PURCHASE) ?? false;
  }
  
  hasPermissionReturnToNegotiation(): boolean {
    return this.userInfo?.hasPermissionOrLegacy(Permission.RETURN_TO_NEGOTIATION) ?? false;
  }

  isOKFinalScore(): boolean {
    return (this.vehicle().finalEvalScore ?? 0) >= 8;
  }
  isWarningFinalScore(): boolean {
    const score = this.vehicle().finalEvalScore ?? 0;
    return score > 5 && score < 8;
  }
  isDangerFinalScore(): boolean {
    return (this.vehicle().finalEvalScore ?? 0) <= 5;
  }

  onOpenPriceModal(): void {
    const dialogRef = this.dialog.open(EvaluationPriceModalComponent, {
      height: "80%",
      width: "90%",
      data: {
        vehicle: this.vehicle(),
        isSold: false
      },
    });
    dialogRef.addPanelClass("dialog-padding");
  }

  testPriceObject(obj: BuyPriceSummary | SellPriceSummary | null | undefined): boolean {
    return obj != null && Object.keys(obj).length > 0;
  }

  isManager(): boolean {
    if (!this.userInfo) return false;

    const hasLegacyRoles =  this.userInfo.hasRoles(Role.ADMIN, Role.DESK_BUY, Role.SELLER_MANAGER) ||
      (this.userInfo.hasRole(Role.EVAL_DEALER_MANAGER) && 
       this.userInfo.evalDealersIds.includes(this.vehicle().evalDealerId)); 

    const hasPermission = this.userInfo.hasAnyPermission(Permission.VIEW_MIN_PRICE_AND_LIST_PRICE);

    return hasLegacyRoles || hasPermission;
  }

  openModalStatus(): void {
    if (this.layoutVars?.alternativeDomain.includes('gestaocpfl')) {
      this.openModernNegotiationStatusDialog();
    } else {
      this.openNegotiationStatusCardDialog();
    }
  }

  openNegotiationStatusCardDialog(): void {
    const v = this.vehicle();
    this.dialog
      .open(DialogNegotiationStatusCardComponent, {
        width: '480px',
        hasBackdrop: true,
        panelClass: 'dialog-negotiation-status-card',
        data: {
          vehicleId: v.id,
          negotiationStatus: v.negotiationStatus,
        },
      })
      .afterClosed()
      .subscribe((result: DialogNegotiationStatusCardResult | undefined) => {
        if (result?.negotiationStatus) {
          v.negotiationStatus = result.negotiationStatus;
        }
      });
  }

  openModernNegotiationStatusDialog(): void {
    const v = this.vehicle();
    this.dialog.open(DialogEvaluationNegotiationStatusComponent, {
      width: "480px",
      hasBackdrop: true,
      data: {
        vehicleId: v.id,
        negotiationStatus: v.negotiationStatus
      },
    })
    .afterClosed()
    .subscribe((result: { negotiationStatus?: NegotiationStatus }) => {
      if (result?.negotiationStatus) {
        this.vehicle().negotiationStatus = result.negotiationStatus;
      }
    });
  }

  hasPermissionArchiveNegotiation(): boolean {
    return this.userInfo?.hasAnyPermissionOrLegacy(Permission.ARCHIVE_EVALUATION) ?? false;
  }

  hasPermissionAddStatusNegotiation(): boolean {
    return this.userInfo?.hasAnyPermissionOrLegacy(Permission.CHANGE_NEGOTIATION_STATUS) ?? false;
  }

  canSeePrices(): boolean {
    return this.userInfo?.hasAnyPermissionOrLegacy(Permission.VIEW_PRICING_PRICE) ?? false;
  }
}
